(ns hai.tasks.drawer.core
  "What a task drawer is left showing, and how its controls change that: whether
   its tray is open, the tab it is on, the search that is out, and what is
   typed, found and picked in each search. Pure — [[hai.tasks.drawer.data]]
   keeps a drawer's in app-db under its key, `[:task uuid]` for a saved task's
   and `[:draft id]` for a new one's."
  (:require
   [clojure.string :as str]))

;;
;; A search
;;

(def blank-search
  "A search as it opens: nothing typed, found or listed. `:latest` is what was
   last sent to be searched for, so an answer to anything older is let go."
  {:text ""
   :latest nil
   :found nil
   :list-open? false
   :dismissed? false
   :index 0})

(def blank-share-search
  "The Share search as it opens: no one picked, joining as viewers."
  (assoc blank-search
         :picked []
         :permission :permission.type/read
         :confirming? false))

(defn type-text
  "`text` typed into the search: the list's live row goes back to the first,
   and the trimmed text is what will be searched for. Typed away to nothing,
   nothing is found or listed."
  [search text]
  (let [trimmed (str/trim text)]
    (cond-> (assoc search :text text :index 0 :latest trimmed)
      (str/blank? trimmed) (assoc :found nil :list-open? false :dismissed? false))))

(defn found
  "What was found for `text`, `payload` with the text it was for, while `text`
   is still what is searched for. The list opens on it unless Escape closed it
   since."
  [search text payload]
  (if (= text (:latest search))
    (cond-> (assoc search :found (assoc payload :text text) :index 0)
      (not (:dismissed? search)) (assoc :list-open? true))
    search))

(defn clear-text
  "Nothing typed: the search back as it opened, bar who is picked and their
   role."
  [search]
  (merge search blank-search))

(defn close-list
  "Escape on the list: it closes, and typing doesn't reopen it."
  [search]
  (assoc search :list-open? false :dismissed? true))

(defn press-field
  "A press on the field, with something typed in it, shows or hides what was
   found for it."
  [search]
  (cond
    (str/blank? (:text search)) search
    (:list-open? search) (close-list search)
    :else (assoc search :list-open? true :dismissed? false)))

(defn leave-field
  "The field losing the caret closes the list."
  [search]
  (assoc search :list-open? false))

(defn point
  "The list's live row, `index` counted from the field."
  [search index]
  (assoc search :index index))

;;
;; The Share search
;;

(defn choose
  "A row chosen from the Share search's list: someone already sharing it is
   shown where they are listed, by the caller, and anyone else waits picked in
   the field. Either way what was typed goes. A row that can't be chosen does
   nothing."
  [search {:keys [recipient holder-key unavailable]}]
  (cond
    unavailable search
    holder-key (clear-text search)
    :else (-> search
              (update :picked conj recipient)
              clear-text)))

(defn unpick
  "The one picked under `recipient-key` taken back out of the field."
  [search recipient-key]
  (update search :picked (fn [picked]
                           (vec (remove #(= recipient-key (:key %)) picked)))))

(defn unpick-last
  "Backspace on an empty field: the last one picked goes."
  [search]
  (cond-> search
    (seq (:picked search)) (update :picked pop)))

(defn clear
  "No one picked and nothing typed: after Add, or the field's ✕."
  [search]
  (-> search
      (assoc :picked [])
      clear-text))

(defn set-permission
  "The role those added join at."
  [search permission]
  (assoc search :permission permission))

(defn keep-editing
  "The question about those picked answered by staying."
  [search]
  (assoc search :confirming? false))

;;
;; The drawer
;;

(defn drawer-key
  "The key a drawer's state is kept under: a saved task's uuid, or a new
   one's draft id."
  [task-uuid draft-id]
  (if task-uuid
    [:task task-uuid]
    [:draft draft-id]))

(defn initial
  "The drawer on `drawer-key` as it first shows: on its Related tab, no search
   out, its tray open — except a saved task's in the `mobile?` layout, which
   starts shut. A new task's always starts open."
  [drawer-key mobile?]
  {:open? (or (= :draft (first drawer-key))
              (not mobile?))
   :tab :related
   :search nil
   :related-search blank-search
   :share-search blank-share-search})

(defn close-search
  "The search that is out put away, and any question about closing it with
   it."
  [drawer]
  (-> drawer
      (assoc :search nil)
      (assoc-in [:share-search :confirming?] false)))

(defn- open-search
  "`tab`'s search brought out, and the drawer turned to the tab. Share's comes
   back as it was left: what is typed and picked waits there until it is added
   or discarded, or the task closes."
  [drawer tab]
  (assoc drawer :tab tab :search tab))

(defn toggle
  "The tray's ✕: it shuts, or opens, and any search goes."
  [drawer]
  (-> drawer
      close-search
      (update :open? not)))

(defn press-tab
  "A press on `tab`: the tab shown shuts or opens the tray, and another turns
   the drawer to itself, open. Any search goes."
  [drawer tab]
  (let [drawer (close-search drawer)]
    (if (= tab (:tab drawer))
      (update drawer :open? not)
      (assoc drawer :tab tab :open? true))))

(defn fold-related
  "A press elsewhere folds Related's search away, and only Related's: by the
   time it lands, Share's may be the one out."
  [drawer]
  (cond-> drawer
    (= :related (:search drawer)) close-search))

(defn ask-to-close-share
  "Share's search asked to close: it does, unless anyone picked hasn't been
   added, and then it asks first."
  [drawer]
  (if (seq (get-in drawer [:share-search :picked]))
    (assoc-in drawer [:share-search :confirming?] true)
    (close-search drawer)))

(defn press-add
  "A press on `tab`'s add button: Related's Add link, or for Share the Share
   button in the task's header. Each tab's field is always at the foot of its
   open drawer, so there the press turns the drawer to it. Otherwise the press
   brings the tab's search out, or puts it away — Share's asking first while
   anyone picked hasn't been added."
  [drawer tab]
  (cond
    (:open? drawer) (-> drawer close-search (assoc :tab tab))
    (not= tab (:search drawer)) (open-search drawer tab)
    (= :share tab) (ask-to-close-share drawer)
    :else (close-search drawer)))

(defn focuses-field?
  "Whether a press on `tab`'s add button leaves the tab's field out, to take
   the caret."
  [drawer tab]
  (or (:open? drawer)
      (not= tab (:search drawer))))
